import i18n from "@/i18n.ts";
import { ApiWorkflowTriggerType } from "@/types/workflows/workflow.base-enum.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import type { RuleRegistryPlaceKey } from "@/types/rule-whitelists.ts";

export enum RuleConditionType {
  all_active_employees = "all_active_employees",
  organization = "organization",
  division = "division",
  department = "department",
  team = "team",
  job_profile = "job_profile",
  employee = "employee",
  employment_type = "employment_type",
  employment_contract = "employment_contract",
  work_pattern = "work_pattern",
  legal_entity = "legal_entity",
  lifecycle_status = "lifecycle_status",
  gender = "gender",
  employment_duration = "employment_duration",
  location = "location",
  custom_field_select = "custom_field_select",
  employees_in_custom_field = "employees_in_custom_field",
  employees_with_employee_in_custom_field = "employees_with_employee_in_custom_field",
  employees_with_me_in_custom_field = "employees_with_me_in_custom_field",

  // only for apply to
  my_subordinates = "my_subordinates",
  my_departments = "my_departments",
  my_department_employees = "my_department_employees",
  my_team = "my_team",
  myself = "myself",

  // only for assign to
  managers = "managers",
  heads_of_departments = "heads_of_departments",
  heads_of_teams = "heads_of_teams",

  // only for flow rule in approval
  direct_manager = "direct_manager",
  manager_of_direct_manager = "manager_of_direct_manager",
  department_manager = "department_manager",
  custom_field_employee_reference = "custom_field_employee_reference",

  // only for workflow apply to box
  affected_employee = "affected_employee",
  affected_employee_direct_manager = "affected_employee_direct_manager",
  affected_employee_subordinates = "affected_employee_subordinates",
  affected_employee_direct_manager_subordinates = "affected_employee_direct_manager_subordinates",
  affected_employee_custom_field_employee_reference = "affected_employee_custom_field_employee_reference",
  affected_employee_custom_field_employee_reference_subordinates = "affected_employee_custom_field_employee_reference_subordinates",

  // only for workflow task assignee / meeting attendee on candidate or job subject
  candidate_job_recruiters = "candidate_job_recruiters",
  candidate_job_stakeholders = "candidate_job_stakeholders",
}

export enum RuleApplySignType {
  include = 1,
  exclude = 2,
}

export type DateConditionOperator = "greater_than" | "less_than";

export interface RelativeDateCondition {
  condition: DateConditionOperator;
  condition_type: "relative_date";
  unit: "month" | "year";
  value: number;
}

export interface SpecificDateCondition {
  condition: DateConditionOperator;
  condition_type: "specific_date";
  date_from: string;
}

export type DateCondition = RelativeDateCondition | SpecificDateCondition;

export enum RuleEmploymentDurationPresetType {
  one_month = "one_month",
  three_months = "three_months",
  six_months = "six_months",
  one_year = "one_year",
  custom_date = "custom_date",
}

export enum RuleEmploymentDurationCalcMode {
  by_period = "by_period",
  specific_date = "specific_date",
}

export interface RuleShort {
  condition_type: RuleConditionType;
  sign: RuleApplySignType;
  condition_ids: (string | number)[];
  /** CF option ids as numbers; strings allowed for JSON/API parity (see SKRuleShort.value_ids). */
  value_ids?: (string | number)[];
  date_condition?: DateCondition;
}

export interface Rule extends RuleShort {
  condition_entities: SelectItem[];
  value_entities: SelectItem[];
}

export interface RuleList {
  rules: Rule[];
}

export interface RuleGroup {
  rules: Rule[];
}

export interface RuleGroupShort {
  rules: RuleShort[];
}

export interface ApplyToRequest {
  rules?: Rule[];
  groups?: RuleGroup[];
  assign_to_groups?: RuleGroup[];
  place?: RuleRegistryPlaceKey;
}

export interface ApplyToFunnelStep {
  rule_index: number;
  employees_count: number;
}

export interface ApplyToGroupFunnel {
  include_steps: ApplyToFunnelStep[];
  exclude_steps: ApplyToFunnelStep[];
  employees_count: number;
}

export interface ApplyToFunnel {
  groups: ApplyToGroupFunnel[];
  employees_count: number;
  multiple_groups_employees_count: number;
  excluded_included_via_other_groups_count: number;
}

export interface ApplyToEmployees {
  employee_ids: number[];
  funnel?: ApplyToFunnel | null;
}

export interface ApiRuleConditionTypeInfo {
  condition_type: string;
  context: RuleConditionContext;
  condition_ids: "none" | "one" | "many";
  value_ids: "none" | "one" | "many";
  requires_date_condition: boolean;
  condition_entity_kind: string | null;
  value_entity_kind: string | null;
  title_key: string;
}

export interface ApiRulePlaceInfo {
  key: string;
  exclude_allowed: boolean;
  is_calculated: boolean;
  condition_types: ApiRuleConditionTypeInfo[];
}

export interface ApiRuleRegistryResponse {
  places: ApiRulePlaceInfo[];
  unavailable_contexts_by_workflow_trigger_type: Record<
    ApiWorkflowTriggerType,
    RuleConditionContext[]
  >;
}

export enum RuleConditionContext {
  none = "none",
  acting_user = "acting_user",
  affected_employee = "affected_employee",
  flow_subject = "flow_subject",
  candidate_job = "candidate_job",
}

export const RuleConditionsItems = () => {
  const groupScope = i18n.t("core:rule_group_scope");
  const groupRelative = i18n.t("core:rule_group_relative");
  const groupByRole = i18n.t("core:rule_group_by_role");
  const groupByAttribute = i18n.t("core:rule_group_by_attribute");
  const groupByAffectedEmployee = i18n.t("core:rule_group_by_affected_employee");
  const groupByCandidateJob = i18n.t("core:rule_group_by_candidate_job");

  return [
    {
      id: RuleConditionType.all_active_employees,
      title: i18n.t("core:all_active_employees"),
      caption: i18n.t("core:rule_all_active_employees_caption"),
      group: groupScope,
    },
    {
      id: RuleConditionType.organization,
      title: i18n.t("core:rule_organization"),
      caption: i18n.t("core:rule_organization_caption"),
      group: groupScope,
    },

    {
      id: RuleConditionType.myself,
      title: i18n.t("core:rule_myself"),
      caption: i18n.t("core:rule_myself_caption"),
      group: groupRelative,
    },
    {
      id: RuleConditionType.my_subordinates,
      title: i18n.t("core:rule_my_subordinates"),
      caption: i18n.t("core:rule_my_subordinates_caption"),
      group: groupRelative,
    },
    {
      id: RuleConditionType.my_departments,
      title: i18n.t("core:rule_my_departments"),
      caption: i18n.t("core:rule_my_departments_caption"),
      group: groupRelative,
    },
    {
      id: RuleConditionType.my_department_employees,
      title: i18n.t("core:rule_my_department_employees"),
      caption: i18n.t("core:rule_my_department_employees_caption"),
      group: groupRelative,
    },
    {
      id: RuleConditionType.my_team,
      title: i18n.t("core:rule_my_team"),
      caption: i18n.t("core:rule_my_team_caption"),
      group: groupRelative,
    },

    {
      id: RuleConditionType.managers,
      title: i18n.t("core:managers"),
      caption: i18n.t("core:rule_managers_caption"),
      group: groupByRole,
    },
    {
      id: RuleConditionType.heads_of_departments,
      title: i18n.t("core:heads_of_departments"),
      caption: i18n.t("core:rule_heads_of_departments_caption"),
      group: groupByRole,
    },
    {
      id: RuleConditionType.heads_of_teams,
      title: i18n.t("core:team_leads"),
      caption: i18n.t("core:rule_heads_of_teams_caption"),
      group: groupByRole,
    },

    {
      id: RuleConditionType.division,
      title: i18n.t("core:division"),
      caption: i18n.t("core:rule_division_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.department,
      title: i18n.t("core:department"),
      caption: i18n.t("core:rule_department_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.team,
      title: i18n.t("core:team"),
      caption: i18n.t("core:rule_team_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.job_profile,
      title: i18n.t("core:job_profile"),
      caption: i18n.t("core:rule_job_profile_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.employee,
      title: i18n.t("core:employee"),
      caption: i18n.t("core:rule_employee_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.direct_manager,
      title: i18n.t("core:rule_direct_manager_title"),
      caption: i18n.t("core:rule_direct_manager_approval_caption"),
      group: groupRelative,
    },
    {
      id: RuleConditionType.manager_of_direct_manager,
      title: i18n.t("core:rule_manager_of_direct_manager_title"),
      caption: i18n.t("core:rule_manager_of_direct_manager_approval_caption"),
      group: groupRelative,
    },
    {
      id: RuleConditionType.department_manager,
      title: i18n.t("core:rule_department_manager_title"),
      caption: i18n.t("core:rule_department_manager_approval_caption"),
      group: groupRelative,
    },
    {
      id: RuleConditionType.custom_field_employee_reference,
      title: i18n.t("core:rule_custom_field_employee_reference"),
      caption: i18n.t("core:rule_custom_field_employee_reference_approval_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.location,
      title: i18n.t("common:location"),
      caption: i18n.t("core:rule_location_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.legal_entity,
      title: i18n.t("core:legal_entity"),
      caption: i18n.t("core:rule_legal_entity_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.lifecycle_status,
      title: i18n.t("core:lifecycle_status"),
      caption: i18n.t("core:rule_lifecycle_status_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.gender,
      title: i18n.t("core:gender"),
      caption: i18n.t("core:rule_gender_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.employment_type,
      title: i18n.t("core:employment_type"),
      caption: i18n.t("core:rule_employment_type_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.employment_contract,
      title: i18n.t("core:employment_contract"),
      caption: i18n.t("core:rule_employment_contract_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.work_pattern,
      title: i18n.t("core:work_pattern"),
      caption: i18n.t("core:rule_work_pattern_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.employment_duration,
      title: i18n.t("core:employment_duration"),
      caption: i18n.t("core:rule_employment_duration_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.custom_field_select,
      title: i18n.t("core:rule_custom_field_select"),
      caption: i18n.t("core:rule_custom_field_select_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.employees_in_custom_field,
      title: i18n.t("core:rule_employees_in_custom_field"),
      caption: i18n.t("core:rule_employees_in_custom_field_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.employees_with_employee_in_custom_field,
      title: i18n.t("core:rule_employees_with_employee_in_custom_field"),
      caption: i18n.t("core:rule_employees_with_employee_in_custom_field_caption"),
      group: groupByAttribute,
    },
    {
      id: RuleConditionType.employees_with_me_in_custom_field,
      title: i18n.t("core:rule_employees_with_me_in_custom_field"),
      caption: i18n.t("core:rule_employees_with_me_in_custom_field_caption"),
      group: groupRelative,
    },

    {
      id: RuleConditionType.affected_employee,
      title: i18n.t("core:rule_affected_employee"),
      caption: i18n.t("core:rule_affected_employee_caption"),
      group: groupByAffectedEmployee,
    },
    {
      id: RuleConditionType.affected_employee_direct_manager,
      title: i18n.t("core:rule_affected_employee_direct_manager"),
      caption: i18n.t("core:rule_affected_employee_direct_manager_caption"),
      group: groupByAffectedEmployee,
    },
    {
      id: RuleConditionType.affected_employee_subordinates,
      title: i18n.t("core:rule_affected_employee_subordinates"),
      caption: i18n.t("core:rule_affected_employee_subordinates_caption"),
      group: groupByAffectedEmployee,
    },
    {
      id: RuleConditionType.affected_employee_direct_manager_subordinates,
      title: i18n.t("core:rule_affected_employee_direct_manager_subordinates"),
      caption: i18n.t("core:rule_affected_employee_direct_manager_subordinates_caption"),
      group: groupByAffectedEmployee,
    },
    {
      id: RuleConditionType.affected_employee_custom_field_employee_reference,
      title: i18n.t("core:rule_affected_employee_custom_field_employee_reference"),
      caption: i18n.t("core:rule_affected_employee_custom_field_employee_reference_caption"),
      group: groupByAffectedEmployee,
    },
    {
      id: RuleConditionType.affected_employee_custom_field_employee_reference_subordinates,
      title: i18n.t("core:rule_affected_employee_custom_field_employee_reference_subordinates"),
      caption: i18n.t(
        "core:rule_affected_employee_custom_field_employee_reference_subordinates_caption"
      ),
      group: groupByAffectedEmployee,
    },

    {
      id: RuleConditionType.candidate_job_recruiters,
      title: i18n.t("core:rule_candidate_job_recruiters"),
      caption: i18n.t("core:rule_candidate_job_recruiters_caption"),
      group: groupByCandidateJob,
    },
    {
      id: RuleConditionType.candidate_job_stakeholders,
      title: i18n.t("core:rule_candidate_job_stakeholders"),
      caption: i18n.t("core:rule_candidate_job_stakeholders_caption"),
      group: groupByCandidateJob,
    },
  ] as SelectItem[];
};

export const RuleSignItems = () => {
  return [
    {
      id: RuleApplySignType.include,
      title: i18n.t("common:equals"),
    },
    {
      id: RuleApplySignType.exclude,
      title: i18n.t("common:dont_equal"),
    },
  ] as SelectItem[];
};

export const RuleEmploymentDurationCalcModeItems = (): SelectItem[] => {
  return [
    {
      id: RuleEmploymentDurationCalcMode.by_period,
      title: i18n.t("core:rule_employment_duration_by_period"),
    },
    {
      id: RuleEmploymentDurationCalcMode.specific_date,
      title: i18n.t("core:rule_employment_duration_specific_date"),
    },
  ];
};

export const RuleEmploymentDurationPeriodConditionItems = (): SelectItem[] => {
  return [
    {
      id: "greater_than" satisfies DateConditionOperator,
      title: i18n.t("core:rule_employment_duration_greater_or_equal"),
    },
    {
      id: "less_than" satisfies DateConditionOperator,
      title: i18n.t("core:rule_employment_duration_less_or_equal"),
    },
  ];
};

export const RuleEmploymentDurationDateConditionItems = (): SelectItem[] => {
  return [
    {
      id: "greater_than" satisfies DateConditionOperator,
      title: i18n.t("core:rule_employment_duration_after_date_inclusive"),
    },
    {
      id: "less_than" satisfies DateConditionOperator,
      title: i18n.t("core:rule_employment_duration_before_date_inclusive"),
    },
  ];
};

export const RuleEmploymentDurationPresetItems = (): SelectItem[] => {
  return [
    {
      id: RuleEmploymentDurationPresetType.one_month,
      title: i18n.t("core:rule_employment_duration_one_month"),
    },
    {
      id: RuleEmploymentDurationPresetType.three_months,
      title: i18n.t("core:rule_employment_duration_three_months"),
    },
    {
      id: RuleEmploymentDurationPresetType.six_months,
      title: i18n.t("core:rule_employment_duration_six_months"),
    },
    {
      id: RuleEmploymentDurationPresetType.one_year,
      title: i18n.t("core:rule_employment_duration_one_year"),
    },
  ];
};

export const getCalcModeFromDateCondition = (
  dc: DateCondition | undefined
): RuleEmploymentDurationCalcMode => {
  if (!dc || dc.condition_type === "relative_date") {
    return RuleEmploymentDurationCalcMode.by_period;
  }
  return RuleEmploymentDurationCalcMode.specific_date;
};

export const getPresetFromDateCondition = (dc: DateCondition): RuleEmploymentDurationPresetType => {
  if (dc.condition_type === "specific_date") return RuleEmploymentDurationPresetType.custom_date;
  if (dc.unit === "year" && dc.value === 1) return RuleEmploymentDurationPresetType.one_year;
  if (dc.unit === "month" && dc.value === 3) return RuleEmploymentDurationPresetType.three_months;
  if (dc.unit === "month" && dc.value === 6) return RuleEmploymentDurationPresetType.six_months;
  return RuleEmploymentDurationPresetType.one_month;
};

export const buildDateCondition = (
  operator: DateConditionOperator,
  preset: RuleEmploymentDurationPresetType,
  dateValue?: string | null
): DateCondition => {
  switch (preset) {
    case RuleEmploymentDurationPresetType.one_month:
      return { condition: operator, condition_type: "relative_date", unit: "month", value: 1 };
    case RuleEmploymentDurationPresetType.three_months:
      return { condition: operator, condition_type: "relative_date", unit: "month", value: 3 };
    case RuleEmploymentDurationPresetType.six_months:
      return { condition: operator, condition_type: "relative_date", unit: "month", value: 6 };
    case RuleEmploymentDurationPresetType.one_year:
      return { condition: operator, condition_type: "relative_date", unit: "year", value: 1 };
    case RuleEmploymentDurationPresetType.custom_date:
      return { condition: operator, condition_type: "specific_date", date_from: dateValue ?? "" };
  }
};

export const formatDateCondition = (dc: DateCondition | undefined): string => {
  if (!dc) return "";

  if (dc.condition_type === "specific_date") {
    const operatorTitle = RuleEmploymentDurationDateConditionItems().find(
      (item) => item.id === dc.condition
    )?.title;
    return `${operatorTitle}: ${dc.date_from}`;
  }

  const operatorTitle = RuleEmploymentDurationPeriodConditionItems().find(
    (item) => item.id === dc.condition
  )?.title;
  const preset = getPresetFromDateCondition(dc);
  const presetTitle = RuleEmploymentDurationPresetItems().find((item) => item.id === preset)?.title;
  return `${operatorTitle}: ${presetTitle}`;
};
